import { Formik, FormikErrors, yupToFormErrors } from 'formik';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useMountedState } from 'react-use';
import { buildLeadFormSchema } from 'lib/validationSchemas';
import { useValidation } from 'lib/hooks/useValidation';
import { LeadFormFields } from 'types/LeadForm';
import { useLeadFormInitialData } from 'lib/hooks/useLeadFormInitialData';

export enum SubmissionState {
  disabled,
  unSubmitted,
  submitted,
  submitting,
}

interface FormManagerProps {
  children: ({ submissionState }: { submissionState: SubmissionState }) => ReactNode;
  showZipCode?: boolean;
  showAddress?: boolean;
  onSubmit: (fields: LeadFormFields) => Promise<void>;
  prefillFromAccount?: boolean;
}

export default function FormManager({
  children,
  showZipCode,
  showAddress,
  onSubmit,
  prefillFromAccount,
}: FormManagerProps): JSX.Element {
  const [submissionState, setSubmissionState] = useState<SubmissionState>(SubmissionState.disabled);
  const isMounted = useMountedState();

  const { validateEmail, validatePhone } = useValidation();
  const validationSchema = buildLeadFormSchema({
    phoneValidator: validatePhone,
    emailValidator: validateEmail,
  });

  const formRef = useRef<any>();

  // Hack to get validation to run at mount time,
  // so that validation errors are always present in onBlur
  // https://github.com/formium/formik/issues/1950#issuecomment-705136567
  useEffect(() => {
    formRef?.current?.validateForm();
  }, []);

  useEffect(() => {
    return () => {
      setSubmissionState(SubmissionState.unSubmitted);
    };
  });

  const {
    name,
    email,
    phone,
    message,
    zip = '',
  } = useLeadFormInitialData({
    fromAccount: prefillFromAccount,
  });

  const initialZipcode = prefillFromAccount ? zip : showZipCode && '';

  return (
    <Formik
      innerRef={formRef}
      enableReinitialize
      initialValues={{
        name,
        email,
        phone,
        message,
        ...(initialZipcode && { zip: initialZipcode }),
        ...(showAddress && { address: '' }),
        sendAlerts: true,
      }}
      onSubmit={async (values): Promise<void> => {
        setSubmissionState(SubmissionState.submitting);
        try {
          await onSubmit(values);
          if (isMounted()) {
            setSubmissionState(SubmissionState.submitted);
          }
        } catch (e) {
          setSubmissionState(SubmissionState.unSubmitted);
        }
      }}
      validationSchema={validationSchema}
      validateOnChange={false}
      validate={async (values): Promise<void | FormikErrors<unknown>> => {
        try {
          await validationSchema.validate(values, {
            abortEarly: false,
            context: {
              zipRequired: showZipCode,
              addressRequired: showAddress,
            },
          });
          setSubmissionState(SubmissionState.unSubmitted);
          return undefined;
        } catch (err) {
          setSubmissionState(SubmissionState.disabled);
          return yupToFormErrors(err);
        }
      }}
    >
      {children({ submissionState })}
    </Formik>
  );
}
